import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import NavLinkAdapter from "@fuse/core/NavLinkAdapter";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { statusEnum } from "utils/enum";

const ACCENT = "#0050ac";

function SupervisorReportsItem({ report }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const date = new Date(report.createdAt || Date.now());

  const formatDate = (d) =>
    `${d.getDate().toString().padStart(2, "0")}/${
      (d.getMonth() + 1).toString().padStart(2, "0")
    }/${d.getFullYear()}`;

  const getStatusChip = (statusId) => {
    if (statusId === statusEnum.APROBADO)
      return { label: "Aprobado", sx: { bgcolor: "#e6f8ef", color: "#157f4a" } };
    if (statusId === statusEnum.RECHAZADO)
      return { label: "Rechazado", sx: { bgcolor: "#ffebea", color: "#b3261e" } };
    if (statusId === statusEnum.PROCESO)
      return { label: "En Proceso", sx: { bgcolor: "#fff8e6", color: "#996600" } };
    return { label: "En Espera", sx: { bgcolor: "#e9f2ff", color: ACCENT } };
  };

  const chip = getStatusChip(report.status_id);

  const getReportPath = () => {
    const basePath = `/supervisorLocals`;
    switch (report.type_id) {
      case 1:
        return `${basePath}/view/${report.id}`;
      case 2:
        return `${basePath}/viewImages/${report.id}`;
      case 5:
        return `${basePath}/viewGeneralDrowsiness/${report.id}`;
      case 6:
        return `${basePath}/viewRoomDrowsiness/${report.id}`;
      case 3:
        return `${basePath}/viewHoteliers/${report.id}`;
      default:
        return `${basePath}/view/${report.id}`;
    }
  };

  return (
    <motion.div whileHover={{ translateY: -4 }} transition={{ duration: 0.18 }}>
      <Card
        component={NavLinkAdapter}
        to={getReportPath()}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "stretch" },
          borderRadius: 2,
          boxShadow: 1,
          textDecoration: "none !important",
          minHeight: 120,
          "&:hover": { boxShadow: 6 },
        }}
      >
        {/* LEFT SIDE */}
        <CardContent
          sx={{
            flex: 1,
            py: { xs: 1.5, sm: 2 },
            px: { xs: 1.75, sm: 2.25 },
            width: "100%",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="center"
            gap={0.5}
          >
            <Tooltip title={report.local?.fantasy_name || ""} arrow>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                noWrap={!isSmall}
                sx={{ color: "text.primary" }}
              >
                {report.local?.fantasy_name || "—"}
              </Typography>
            </Tooltip>

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{ mt: 0.5 }}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <FuseSvgIcon size={16} color="disabled">
                  material-solid:calendar_today
                </FuseSvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(date)}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.5}>
                <FuseSvgIcon size={16} color="disabled">
                  material-solid:assignment
                </FuseSvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {report.type_audit?.value || report.name}
                </Typography>
              </Box>

              {report.user_name && (
                <Typography variant="caption" color="text.secondary">
                  • {report.user_name}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>

        {/* DIVIDER - se oculta en mobile */}
        {!isSmall && <Divider orientation="vertical" flexItem />}

        {/* RIGHT SIDE */}
        <Box
          sx={{
            width: { xs: "100%", sm: 150 },
            minWidth: { sm: 120 },
            display: "flex",
            flexDirection: { xs: "row", sm: "column" },
            justifyContent: { xs: "space-between", sm: "center" },
            alignItems: { xs: "center", sm: "flex-end" },
            gap: 1,
            py: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 2 },
            borderTop: { xs: "1px solid #eee", sm: "none" },
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ alignSelf: { sm: "flex-end", xs: "center" } }}
          >
            N° {report.id}
          </Typography>

          <Chip
            label={chip.label}
            size="small"
            sx={{
              fontWeight: 700,
              borderRadius: 1,
              px: 1.2,
              py: 0.4,
              ...chip.sx,
            }}
          />
        </Box>
      </Card>
    </motion.div>
  );
}

export default SupervisorReportsItem;
